import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { blogArticles } from './article-data';
import SEOHead from '../../components/SEOHead';

export default function ArticlePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const article = blogArticles.find(a => a.id === Number(id));
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!article) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-[#1A4D4D] mb-4">Article Not Found</h1>
          <Link to="/blog" className="text-[#FF6B6B] hover:underline">
            Return to Blog
          </Link>
        </div>
      </div>
    );
  }

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    datePublished: article.date,
    dateModified: article.date,
    author: {
      '@type': 'Person',
      name: 'Interior Decor Solutions Team',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Interior Decor Solutions',
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/logo.png`,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteUrl}/blog/${article.id}`,
    },
    articleSection: article.category,
    keywords: `${article.category}, home decor, interior design, styling tips`,
  };

  const relatedArticles = blogArticles
    .filter(a => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const handleNewsletterSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    const formData = new URLSearchParams();
    formData.append('email', email);

    try {
      const response = await fetch('https://readdy.ai/api/form/d5dbhf1dn6dhfpiac24g', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formData.toString(),
      });

      if (response.ok) {
        setSubmitMessage('Thank you for subscribing!');
        setEmail('');
      } else {
        setSubmitMessage('Something went wrong. Please try again.');
      }
    } catch (error) {
      setSubmitMessage('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title={`${article.title} | Interior Decor Solutions Blog`}
        description={article.excerpt}
        keywords={`${article.category}, home decor, interior design, styling tips, ${article.title}`}
        canonical={`${siteUrl}/blog/${article.id}`}
        ogType="article"
        ogImage={article.image}
        article={{
          publishedTime: article.date,
          modifiedTime: article.date,
          author: 'Interior Decor Solutions Team',
          section: article.category,
          tags: [article.category, 'home decor', 'interior design'],
        }}
        jsonLd={jsonLd}
      />
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur-sm shadow-sm">
        <div className="px-4 md:px-8 lg:px-20 h-[70px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <h1 className="text-[#1A4D4D] text-xl md:text-2xl lg:text-3xl font-bold tracking-tight" style={{ fontFamily: 'Inter, sans-serif', letterSpacing: '-0.02em' }}>
              Interior Decor Solutions
            </h1>
          </Link>
          
          <div className="hidden lg:flex items-center gap-8">
            <Link to="/" className="text-[#1A4D4D] font-medium text-base hover:text-[#FF6B6B] transition-colors whitespace-nowrap">Home</Link>
            <Link to="/blog" className="text-[#FF6B6B] font-medium text-base whitespace-nowrap">Blog</Link>
            <Link to="/reviews" className="text-[#1A4D4D] font-medium text-base hover:text-[#FF6B6B] transition-colors whitespace-nowrap">Reviews</Link>
            <Link to="/about" className="text-[#1A4D4D] font-medium text-base hover:text-[#FF6B6B] transition-colors whitespace-nowrap">About</Link>
          </div>

          <button className="hidden lg:flex bg-[#1A4D4D] text-white px-6 py-2.5 rounded-full font-medium text-sm hover:bg-[#0F3333] transition-all whitespace-nowrap items-center gap-2">
            <i className="ri-mail-line"></i>
            Get Ideas
          </button>

          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center text-[#1A4D4D]"
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="lg:hidden bg-white border-t border-gray-200">
            <div className="px-4 py-4 space-y-3">
              <Link to="/" className="block text-[#1A4D4D] font-medium text-base py-2">Home</Link>
              <Link to="/blog" className="block text-[#FF6B6B] font-medium text-base py-2">Blog</Link>
              <Link to="/reviews" className="block text-[#1A4D4D] font-medium text-base py-2">Reviews</Link>
              <Link to="/about" className="block text-[#1A4D4D] font-medium text-base py-2">About</Link>
            </div>
          </div>
        )}
      </nav>

      {/* Article Header */}
      <article className="pt-24 md:pt-32 pb-12 md:pb-20 mt-[70px]">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <Link to="/blog" className="inline-flex items-center gap-2 text-[#1A4D4D] hover:text-[#FF6B6B] transition-colors mb-6 text-sm md:text-base">
            <i className="ri-arrow-left-line"></i>
            Back to Blog
          </Link>

          <span className="inline-block px-3 md:px-4 py-1 md:py-1.5 bg-[#FF6B6B]/10 text-[#FF6B6B] text-xs font-semibold uppercase rounded-full mb-4">
            {article.category}
          </span>

          <h1 className="text-[#1A4D4D] text-3xl md:text-5xl lg:text-6xl font-bold mb-4 md:mb-6 leading-tight">
            {article.title}
          </h1>

          <div className="flex items-center gap-4 text-gray-600 text-sm md:text-base mb-8 md:mb-12">
            <span>{article.date}</span>
            <span>•</span>
            <span>{article.readTime}</span>
          </div>

          <div className="w-full h-[300px] md:h-[500px] rounded-2xl overflow-hidden mb-8 md:mb-12">
            <img 
              src={article.image}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>

        {/* Article Content */}
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <div className="prose prose-lg max-w-none">
            <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-8">
              {article.content.introduction}
            </p>

            {article.content.sections.map((section, index) => (
              <div key={index} className="mb-8 md:mb-10">
                <h2 className="text-[#1A4D4D] text-2xl md:text-3xl font-bold mb-4">
                  {section.heading}
                </h2>
                <p className="text-gray-700 text-base md:text-lg leading-relaxed mb-4">
                  {section.content}
                </p>
                {article.id === 1 && index === 0 && (
                  <a
                    href="https://amzn.to/4jZqPXe"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 bg-[#FF6B6B] text-white px-6 py-3 rounded-lg font-medium text-base hover:bg-[#FF5252] transition-all whitespace-nowrap"
                  >
                    Floating Shelves
                    <i className="ri-arrow-right-line"></i>
                  </a>
                )}
                {article.id === 1 && index === 1 && (
                  <div className="flex flex-wrap gap-3">
                    <a
                      href="https://amzn.to/4qHln8X"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#FF6B6B] text-white px-6 py-3 rounded-lg font-medium text-base hover:bg-[#FF5252] transition-all whitespace-nowrap"
                    >
                      Wall Mount Vanity
                      <i className="ri-arrow-right-line"></i>
                    </a>
                    <a
                      href="https://amzn.to/49qfA0x"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#FF6B6B] text-white px-6 py-3 rounded-lg font-medium text-base hover:bg-[#FF5252] transition-all whitespace-nowrap"
                    >
                      Pedestal Sink
                      <i className="ri-arrow-right-line"></i>
                    </a>
                    <a
                      href="https://amzn.to/3Z34CJB"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#FF6B6B] text-white px-6 py-3 rounded-lg font-medium text-base hover:bg-[#FF5252] transition-all whitespace-nowrap"
                    >
                      Modern Fixture
                      <i className="ri-arrow-right-line"></i>
                    </a>
                    <a
                      href="https://amzn.to/4qRIqOF"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#FF6B6B] text-white px-6 py-3 rounded-lg font-medium text-base hover:bg-[#FF5252] transition-all whitespace-nowrap"
                    >
                      Wall-hung Toilets
                      <i className="ri-arrow-right-line"></i>
                    </a>
                    <a
                      href="https://amzn.to/49Jt0Gl"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#FF6B6B] text-white px-6 py-3 rounded-lg font-medium text-base hover:bg-[#FF5252] transition-all whitespace-nowrap"
                    >
                      Compact Bathroom Faucet
                      <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                )}
                {article.id === 1 && index === 2 && (
                  <div className="flex flex-wrap gap-3">
                    <a
                      href="https://amzn.to/452NtTT"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#FF6B6B] text-white px-6 py-3 rounded-lg font-medium text-base hover:bg-[#FF5252] transition-all whitespace-nowrap"
                    >
                      Vanity Lighting
                      <i className="ri-arrow-right-line"></i>
                    </a>
                    <a
                      href="https://amzn.to/4pQFaCl"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#FF6B6B] text-white px-6 py-3 rounded-lg font-medium text-base hover:bg-[#FF5252] transition-all whitespace-nowrap"
                    >
                      Recessed Bathroom Lighting
                      <i className="ri-arrow-right-line"></i>
                    </a>
                    <a
                      href="https://amzn.to/3Yxo15i"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#FF6B6B] text-white px-6 py-3 rounded-lg font-medium text-base hover:bg-[#FF5252] transition-all whitespace-nowrap"
                    >
                      LED Bathroom Lighting
                      <i className="ri-arrow-right-line"></i>
                    </a>
                    <a
                      href="https://amzn.to/4sxIAfH"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#FF6B6B] text-white px-6 py-3 rounded-lg font-medium text-base hover:bg-[#FF5252] transition-all whitespace-nowrap"
                    >
                      Skylights
                      <i className="ri-arrow-right-line"></i>
                    </a>
                    <a
                      href="https://amzn.to/4pAoY81"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#FF6B6B] text-white px-6 py-3 rounded-lg font-medium text-base hover:bg-[#FF5252] transition-all whitespace-nowrap"
                    >
                      Backlit Bathroom Mirrors
                      <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                )}
                {article.id === 1 && index === 3 && (
                  <div className="flex flex-wrap gap-3">
                    <a
                      href="https://amzn.to/4q8gt54"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#FF6B6B] text-white px-6 py-3 rounded-lg font-medium text-base hover:bg-[#FF5252] transition-all whitespace-nowrap"
                    >
                      Subway Tiles
                      <i className="ri-arrow-right-line"></i>
                    </a>
                    <a
                      href="https://amzn.to/3LmPQdE"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#FF6B6B] text-white px-6 py-3 rounded-lg font-medium text-base hover:bg-[#FF5252] transition-all whitespace-nowrap"
                    >
                      Patterned Bathroom Tiles
                      <i className="ri-arrow-right-line"></i>
                    </a>
                    <a
                      href="https://amzn.to/3LmPQdE"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#FF6B6B] text-white px-6 py-3 rounded-lg font-medium text-base hover:bg-[#FF5252] transition-all whitespace-nowrap"
                    >
                      Glossy Bathroom Tiles
                      <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                )}
                {article.id === 1 && index === 4 && (
                  <div className="flex flex-wrap gap-3">
                    <a
                      href="https://amzn.to/4q8TuXy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#FF6B6B] text-white px-6 py-3 rounded-lg font-medium text-base hover:bg-[#FF5252] transition-all whitespace-nowrap"
                    >
                      Large Bathroom Mirrors
                      <i className="ri-arrow-right-line"></i>
                    </a>
                    <a
                      href="https://amzn.to/3YtNY5M"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#FF6B6B] text-white px-6 py-3 rounded-lg font-medium text-base hover:bg-[#FF5252] transition-all whitespace-nowrap"
                    >
                      Frameless Bathroom Mirrors
                      <i className="ri-arrow-right-line"></i>
                    </a>
                    <a
                      href="https://amzn.to/3Z3zn0W"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#FF6B6B] text-white px-6 py-3 rounded-lg font-medium text-base hover:bg-[#FF5252] transition-all whitespace-nowrap"
                    >
                      Medicine Cabinet Mirrors
                      <i className="ri-arrow-right-line"></i>
                    </a>
                    <a
                      href="https://amzn.to/4pBentf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#FF6B6B] text-white px-6 py-3 rounded-lg font-medium text-base hover:bg-[#FF5252] transition-all whitespace-nowrap"
                    >
                      Mirrored Bathroom Cabinets
                      <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                )}
                {/* Living Room Article Product Buttons */}
                {article.id === 4 && index === 0 && (
                  <div className="flex flex-wrap gap-3">
                    <a
                      href="https://amzn.to/4tcAppw"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#FF6B6B] text-white px-6 py-3 rounded-lg font-medium text-base hover:bg-[#FF5252] transition-all whitespace-nowrap"
                    >
                      Arc Floor Lamps
                      <i className="ri-arrow-right-line"></i>
                    </a>
                    <a
                      href="https://amzn.to/4jUslVX"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#FF6B6B] text-white px-6 py-3 rounded-lg font-medium text-base hover:bg-[#FF5252] transition-all whitespace-nowrap"
                    >
                      Tripod Floor Lamps
                      <i className="ri-arrow-right-line"></i>
                    </a>
                    <a
                      href="https://amzn.to/3LIDQ6r"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#FF6B6B] text-white px-6 py-3 rounded-lg font-medium text-base hover:bg-[#FF5252] transition-all whitespace-nowrap"
                    >
                      Modern Floor Lamps
                      <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                )}
                {article.id === 4 && index === 1 && (
                  <div className="flex flex-wrap gap-3">
                    <a
                      href="https://amzn.to/49Hpgp2"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#FF6B6B] text-white px-6 py-3 rounded-lg font-medium text-base hover:bg-[#FF5252] transition-all whitespace-nowrap"
                    >
                      Abstract Canvas Art
                      <i className="ri-arrow-right-line"></i>
                    </a>
                    <a
                      href="https://amzn.to/3YX7lo8"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#FF6B6B] text-white px-6 py-3 rounded-lg font-medium text-base hover:bg-[#FF5252] transition-all whitespace-nowrap"
                    >
                      Gallery Wall Sets
                      <i className="ri-arrow-right-line"></i>
                    </a>
                    <a
                      href="https://amzn.to/3YZAfnv"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#FF6B6B] text-white px-6 py-3 rounded-lg font-medium text-base hover:bg-[#FF5252] transition-all whitespace-nowrap"
                    >
                      Botanical Prints
                      <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                )}
                {article.id === 4 && index === 2 && (
                  <div className="flex flex-wrap gap-3">
                    <a
                      href="https://amzn.to/4bim44e"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#FF6B6B] text-white px-6 py-3 rounded-lg font-medium text-base hover:bg-[#FF5252] transition-all whitespace-nowrap"
                    >
                      Floating Shelves
                      <i className="ri-arrow-right-line"></i>
                    </a>
                    <a
                      href="https://amzn.to/45uJn7d"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#FF6B6B] text-white px-6 py-3 rounded-lg font-medium text-base hover:bg-[#FF5252] transition-all whitespace-nowrap"
                    >
                      Wood Floating Shelves
                      <i className="ri-arrow-right-line"></i>
                    </a>
                    <a
                      href="https://amzn.to/49RNTxY"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#FF6B6B] text-white px-6 py-3 rounded-lg font-medium text-base hover:bg-[#FF5252] transition-all whitespace-nowrap"
                    >
                      LED Strip Lights
                      <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                )}
                {article.id === 4 && index === 3 && (
                  <div className="flex flex-wrap gap-3">
                    <a
                      href="https://amzn.to/4jYy50T"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#FF6B6B] text-white px-6 py-3 rounded-lg font-medium text-base hover:bg-[#FF5252] transition-all whitespace-nowrap"
                    >
                      8x10 Area Rugs
                      <i className="ri-arrow-right-line"></i>
                    </a>
                    <a
                      href="https://amzn.to/3NJ4kVR"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#FF6B6B] text-white px-6 py-3 rounded-lg font-medium text-base hover:bg-[#FF5252] transition-all whitespace-nowrap"
                    >
                      Geometric Rugs
                      <i className="ri-arrow-right-line"></i>
                    </a>
                    <a
                      href="https://amzn.to/4adh7bS"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#FF6B6B] text-white px-6 py-3 rounded-lg font-medium text-base hover:bg-[#FF5252] transition-all whitespace-nowrap"
                    >
                      Wool Area Rugs
                      <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                )}
                {article.id === 4 && index === 4 && (
                  <div className="flex flex-wrap gap-3">
                    <a
                      href="https://amzn.to/3Zt47J1"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#FF6B6B] text-white px-6 py-3 rounded-lg font-medium text-base hover:bg-[#FF5252] transition-all whitespace-nowrap"
                    >
                      Accent Chairs
                      <i className="ri-arrow-right-line"></i>
                    </a>
                    <a
                      href="https://amzn.to/4qgB1rz"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#FF6B6B] text-white px-6 py-3 rounded-lg font-medium text-base hover:bg-[#FF5252] transition-all whitespace-nowrap"
                    >
                      Velvet Accent Chairs
                      <i className="ri-arrow-right-line"></i>
                    </a>
                    <a
                      href="https://amzn.to/3LM1TRY"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-[#FF6B6B] text-white px-6 py-3 rounded-lg font-medium text-base hover:bg-[#FF5252] transition-all whitespace-nowrap"
                    >
                      Swivel Accent Chairs
                      <i className="ri-arrow-right-line"></i>
                    </a>
                  </div>
                )}
              </div>
            ))}

            <div className="mt-10 md:mt-12 pt-8 md:pt-10 border-t border-gray-200">
              <h2 className="text-[#1A4D4D] text-2xl md:text-3xl font-bold mb-4">
                Conclusion
              </h2>
              <p className="text-gray-700 text-base md:text-lg leading-relaxed">
                {article.content.conclusion}
              </p>
              <p className="text-gray-500 text-xs md:text-sm italic mt-4">
                As an Amazon Associate, we earn from qualifying purchases at no additional cost to you.
              </p>
            </div>
          </div>
        </div>
      </article>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="py-12 md:py-20 px-4 md:px-8 lg:px-20 bg-[#F9F7F4]">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-[#1A4D4D] text-3xl md:text-4xl font-bold mb-8 md:mb-12">
              Related Articles
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {relatedArticles.map((post) => (
                <div 
                  key={post.id} 
                  onClick={() => navigate(`/blog/${post.id}`)}
                  className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all hover:-translate-y-1 cursor-pointer border border-gray-100"
                >
                  <div className="w-full h-[200px] md:h-[250px]">
                    <img 
                      src={post.image}
                      alt={post.title}
                      className="w-full h-full object-cover object-top"
                    />
                  </div>
                  <div className="p-4 md:p-6">
                    <span className="inline-block px-2.5 md:px-3 py-1 bg-[#FF6B6B]/10 text-[#FF6B6B] text-xs font-semibold uppercase rounded-full mb-3">
                      {post.category}
                    </span>
                    <h3 className="text-[#1A4D4D] text-lg md:text-xl font-semibold mb-2 line-clamp-2 hover:text-[#FF6B6B] transition-colors">
                      {post.title}
                    </h3>
                    <p className="text-gray-600 text-sm md:text-base leading-relaxed line-clamp-2 mb-3">
                      {post.excerpt}
                    </p>
                    <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                      <span className="text-gray-500 text-xs md:text-sm">{post.date}</span>
                      <span className="text-[#1A4D4D] font-medium hover:text-[#0F3333] transition-colors whitespace-nowrap text-sm">
                        Read More →
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Newsletter CTA */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-[#1A4D4D]">
          <div className="absolute inset-0 opacity-10">
            <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
              <pattern id="world-pattern" x="0" y="0" width="100" height="100" patternUnits="userSpaceOnUse">
                <circle cx="50" cy="50" r="2" fill="white" opacity="0.3"/>
                <circle cx="20" cy="30" r="1.5" fill="white" opacity="0.2"/>
                <circle cx="80" cy="70" r="1.5" fill="white" opacity="0.2"/>
              </pattern>
              <rect width="100%" height="100%" fill="url(#world-pattern)"/>
            </svg>
          </div>
        </div>

        <div className="relative z-10 max-w-2xl mx-auto text-center px-4 md:px-8">
          <h2 className="text-white text-3xl md:text-4xl lg:text-5xl font-light mb-3 md:mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            Never miss a cozy idea
          </h2>
          <p className="text-white/80 text-base md:text-lg mb-6 md:mb-8">
            Weekly decor tips & exclusive deals
          </p>
          
          <form onSubmit={handleNewsletterSubmit} className="flex items-center justify-center gap-0 max-w-sm mx-auto" data-readdy-form id="newsletter-article">
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email"
              required
              className="flex-1 h-[50px] md:h-[54px] px-4 md:px-5 bg-transparent border-2 border-white/40 rounded-l-full text-white placeholder-white/60 focus:outline-none focus:border-white/60 text-sm"
            />
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-[50px] h-[50px] md:w-[54px] md:h-[54px] bg-[#1A4D4D] rounded-r-full flex items-center justify-center text-white hover:bg-[#0F3333] transition-all disabled:opacity-50 whitespace-nowrap"
            >
              <i className="ri-notification-line text-lg md:text-xl"></i>
            </button>
          </form>
          {submitMessage && (
            <p className={`mt-4 text-sm ${submitMessage.includes('Thank') ? 'text-white' : 'text-[#FF6B6B]'}`}>
              {submitMessage}
            </p>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="relative bg-[#1A4D4D] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-[0.08]">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <pattern id="home-pattern" x="0" y="0" width="80" height="80" patternUnits="userSpaceOnUse">
              <path d="M40 20 L50 30 L50 45 L30 45 L30 30 Z" stroke="white" fill="none" strokeWidth="1"/>
              <circle cx="15" cy="60" r="3" stroke="white" fill="none" strokeWidth="1"/>
              <rect x="60" y="55" width="8" height="8" stroke="white" fill="none" strokeWidth="1"/>
            </pattern>
            <rect width="100%" height="100%" fill="url(#home-pattern)"/>
          </svg>
        </div>

        <div className="relative z-10 px-4 md:px-8 lg:px-20 py-12 md:py-20">
          <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-10 gap-8 md:gap-10">
            <div className="lg:col-span-3">
              <div className="flex items-center gap-2 mb-6">
                <img src="https://public.readdy.ai/ai/img_res/9e46e3d9-adac-48a0-871c-356cdcae054d.png" alt="Simple Home Decor" className="h-8 w-auto brightness-0 invert" />
              </div>
              
              <p className="text-white text-2xl md:text-3xl font-light leading-tight" style={{ fontFamily: 'Georgia, serif' }}>
                Simple touches.<br />
                Cozy homes.
              </p>
            </div>

            <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
              <div>
                <h4 className="text-white text-base md:text-lg font-semibold mb-3 md:mb-4">Shop</h4>
                <ul className="space-y-2 md:space-y-3">
                  <li><Link to="/reviews" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Living Room</Link></li>
                  <li><Link to="/reviews" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Bedroom</Link></li>
                  <li><Link to="/reviews" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Kitchen</Link></li>
                </ul>
              </div>

              <div>
                <h4 className="text-white text-base md:text-lg font-semibold mb-3 md:mb-4">Learn</h4>
                <ul className="space-y-2 md:space-y-3">
                  <li><Link to="/blog" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Blog</Link></li>
                  <li><Link to="/reviews" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Reviews</Link></li>
                  <li><Link to="/blog" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Guides</Link></li>
                </ul>
              </div>

              <div>
                <h4 className="text-white text-base md:text-lg font-semibold mb-3 md:mb-4">Company</h4>
                <ul className="space-y-2 md:space-y-3">
                  <li><Link to="/about" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">About</Link></li>
                  <li><a href="#" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Contact</a></li>
                  <li><Link to="/affiliate-disclosure" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Affiliate Disclosure</Link></li>
                </ul>
              </div>

              <div>
                <h4 className="text-white text-base md:text-lg font-semibold mb-3 md:mb-4">Connect</h4>
                <ul className="space-y-2 md:space-y-3">
                  <li><a href="#" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Pinterest</a></li>
                  <li><a href="#" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Instagram</a></li>
                  <li><a href="#" className="text-white/80 hover:text-white transition-colors text-sm md:text-base whitespace-nowrap">Newsletter</a></li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="relative z-10 border-t border-white/20 px-4 md:px-8 lg:px-20 py-8 md:py-12">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
            <p className="text-white/80 text-xs md:text-sm text-center md:text-left">
              © 2026 Simple Home Decor. All rights reserved.
            </p>

            <div className="flex items-center gap-3 md:gap-4">
              <a href="#" className="w-9 h-9 md:w-10 md:h-10 border border-white/40 rounded-lg flex items-center justify-center hover:bg-white/10 transition-all whitespace-nowrap">
                <i className="ri-pinterest-line text-base md:text-lg"></i>
              </a>
              <a href="#" className="w-9 h-9 md:w-10 md:h-10 border border-white/40 rounded-lg flex items-center justify-center hover:bg-white/10 transition-all whitespace-nowrap">
                <i className="ri-instagram-line text-base md:text-lg"></i>
              </a>
              <a href="#" className="w-9 h-9 md:w-10 md:h-10 border border-white/40 rounded-lg flex items-center justify-center hover:bg-white/10 transition-all whitespace-nowrap">
                <i className="ri-facebook-line text-base md:text-lg"></i>
              </a>
              <a href="https://www.youtube.com/@InteriorDecorSolutions" target="_blank" rel="noopener noreferrer" className="w-9 h-9 md:w-10 md:h-10 border border-white/40 rounded-lg flex items-center justify-center hover:bg-white/10 transition-all whitespace-nowrap">
                <i className="ri-youtube-line text-base md:text-lg"></i>
              </a>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4 text-white/80 text-xs md:text-sm">
              <a 
                href="#" 
                onClick={(e) => {
                  e.preventDefault();
                  window.REACT_APP_NAVIGATE?.('/privacy');
                }}
                className="hover:text-white transition-colors whitespace-nowrap cursor-pointer"
              >
                Privacy Policy
              </a>
              <span className="hidden md:inline">|</span>
              <a 
                href="#" 
                onClick={(e) => {
                  e.preventDefault();
                  window.REACT_APP_NAVIGATE?.('/terms');
                }}
                className="hover:text-white transition-colors whitespace-nowrap cursor-pointer"
              >
                Terms of Use
              </a>
              <span className="hidden md:inline">|</span>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors whitespace-nowrap">Website Builder</a>
            </div>
          </div>
          
          <div className="max-w-7xl mx-auto mt-6 pt-6 border-t border-white/10">
            <p className="text-white/60 text-xs text-center leading-relaxed">
              Affiliate Disclosure: This website contains affiliate links. We may earn a commission when you make a purchase through these links at no additional cost to you.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
